import type { ColumnDef } from '@tanstack/react-table'
import type { Transaction } from '@/hooks/useTransactions'
import { Badge } from '@/components/ui/badge'
import { Bitcoin, DollarSign, CircleDollarSign } from 'lucide-react'

const tokenIcons = {
    BTC: Bitcoin,
    USDT: DollarSign,
    USDC: CircleDollarSign,
    ETH: () => <span className="text-xs font-bold">Ξ</span>,
    DOGE: () => <span className="text-xs font-bold">Ð</span>,
}

const txnTypeColors = {
    Buy: 'bg-blue-100 text-blue-700 hover:bg-blue-100',
    Sell: 'bg-orange-100 text-orange-700 hover:bg-orange-100',
    Convert: 'bg-purple-100 text-purple-700 hover:bg-purple-100',
    Receive: 'bg-cyan-100 text-cyan-700 hover:bg-cyan-100',
    Send: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100',
}

const statusColors = {
    Completed: 'bg-green-100 text-green-700 hover:bg-green-100',
    Pending: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100',
    Failed: 'bg-red-100 text-red-700 hover:bg-red-100',
    Cancelled: 'bg-gray-100 text-gray-700 hover:bg-gray-100',
}

export const transactionColumns: ColumnDef<Transaction>[] = [
    {
        accessorKey: 'id',
        header: 'Txn ID',
        cell: ({ row }) => (
            <span className="font-mono text-xs">#{row.original.id}</span>
        ),
    },
    {
        accessorKey: 'date',
        header: 'Date',
        cell: ({ row }) => (
            <span className="text-sm text-muted-foreground">
                {row.original.date}
            </span>
        ),
    },
    {
        accessorKey: 'accountName',
        header: 'Account name',
        cell: ({ row }) => (
            <span className="text-sm">{row.original.accountName}</span>
        ),
    },
    {
        accessorKey: 'walletAddress',
        header: 'Wallet Address',
        cell: ({ row }) => (
            <span className="font-mono text-xs text-muted-foreground">
                {row.original.walletAddress}
            </span>
        ),
    },
    {
        accessorKey: 'txnType',
        header: 'Txn Type',
        cell: ({ row }) => {
            const type = row.original.txnType
            return (
                <Badge variant="secondary" className={txnTypeColors[type]}>
                    {type}
                </Badge>
            )
        },
    },
    {
        accessorKey: 'amount',
        header: 'Amount',
        cell: ({ row }) => (
            <span className="text-sm font-medium">{row.original.amount}</span>
        ),
    },
    {
        accessorKey: 'token',
        header: 'Token',
        cell: ({ row }) => {
            const token = row.original.token
            const Icon = tokenIcons[token] || DollarSign
            return (
                <div className="flex items-center gap-2">
                    <div className="h-6 w-6 rounded-full bg-orange-100 flex items-center justify-center">
                        <Icon className="h-3 w-3 text-orange-600" />
                    </div>
                    <span className="text-sm font-medium">{token}</span>
                </div>
            )
        },
    },
    {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
            const status = row.original.status
            return (
                <Badge variant="secondary" className={statusColors[status]}>
                    {status}
                </Badge>
            )
        },
    },
]
